import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { Checkbox } from "../primitives/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";

type ColocaCheckboxFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Label rendered to the right of the checkbox. Clicking it toggles the checkbox. */
  label?: ReactNode;
  /** Helper text rendered below the label. Use for consequences or constraints of checking. */
  description?: ReactNode;
  disabled?: boolean;
};

/**
 * Controlled checkbox wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any boolean toggle that requires explicit user acknowledgment — e.g.
 * accepting terms, opting in to a feature. The label is rendered to the right and remains clickable.
 * Validation errors are displayed automatically via `<FormMessage>`.
 */
export function ColocaCheckboxField({ name, label, description, disabled }: ColocaCheckboxFieldProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row gap-2" data-disabled={disabled || undefined}>
          <FormControl>
            <Checkbox
              className="mt-0.5"
              checked={field.value}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
            />
          </FormControl>
          <div className="grid gap-1 leading-none pt-0.5">
            {/*
              `label` takes a ReactNode, and the `<label>` it lands in is a flex row
              (`primitives/label.tsx`: `flex items-center gap-2`, no wrap). So a composite label — text,
              a link, a full stop — arrives as SEVERAL flex items competing for one line that cannot
              wrap, rather than one sentence that can. The consent box shipped that way and broke on a
              phone: the anchor was blockified and squeezed until its trailing icon dropped to a line of
              its own, "Acepto los" split into two narrow lines, and the full stop floated off to the
              right on its own `gap-2`, centred against an anchor that was by then two lines tall.

              One block-level child restores normal inline flow INSIDE the label. The wrapper is additive
              on purpose: overriding the label to `display: block` fixes the same symptom and silently
              breaks `FormLabel`'s required asterisk, a sibling pseudo-element whose `-ml-1.5` exists to
              cancel that very `gap-2`. A label that is a single string renders identically either way.
            */}
            {label && (
              <FormLabel>
                <span className="block">{label}</span>
              </FormLabel>
            )}
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}
